import { Link, useNavigate } from 'react-router-dom';
import { useAuth, homeFor } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import SideColumn from './SideColumn';
import { Button } from './ui/button';
import { Volleyball, LogOut, BookOpen, NotebookPen, Users, BarChart3, Shield, Sparkles, User, Briefcase } from 'lucide-react';

const NAV_BY_ROLE = {
  jeune: [
    { to: '/tableau-de-bord', labelKey: 'nav.chapters', icon: BookOpen, testid: 'nav-chapters' },
    { to: '/carnet', labelKey: 'nav.diary', icon: NotebookPen, testid: 'nav-diary' },
    { to: '/mon-parcours', labelKey: 'nav.synthesis', icon: Sparkles, testid: 'nav-synthesis' },
  ],
  parent: [{ to: '/parent', labelKey: 'nav.parent', icon: Users, testid: 'nav-parent' }],
  educateur: [{ to: '/educateur', labelKey: 'nav.educator', icon: BarChart3, testid: 'nav-educator' }],
  admin: [
    { to: '/admin', labelKey: 'nav.admin', icon: Shield, testid: 'nav-admin' },
    { to: '/admin/crm', labelKey: 'nav.crm', icon: Briefcase, testid: 'nav-crm' },
  ],
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-nuit-900">
      <header className="sticky top-0 z-40 bg-nuit-900/95 backdrop-blur border-b border-nuit-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">
          <Link to={homeFor(user?.role)} data-testid="nav-brand"
            className="flex items-center gap-2 font-fredoka font-semibold text-lg text-or-400">
            <span className="w-9 h-9 rounded-full bg-or-400 flex items-center justify-center">
              <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
            </span>
            <span className="hidden sm:inline">On croit qu'on joue !</span>
          </Link>
          <nav className="flex items-center gap-0.5 sm:gap-2">
            {(NAV_BY_ROLE[user?.role] || []).map((item) => (
              <Link key={item.to} to={item.to} data-testid={item.testid}>
                <Button variant="ghost" className="rounded-full text-creme hover:bg-nuit-700 hover:text-or-300 gap-2 px-2 sm:px-4">
                  <item.icon className="w-4 h-4" strokeWidth={2.5} />
                  <span className="hidden sm:inline">{t(item.labelKey)}</span>
                </Button>
              </Link>
            ))}
            <LanguageSwitcher />
            <span data-testid="nav-user-name" className="hidden md:inline text-sm font-bold text-brume px-2">
              {user?.name}
            </span>
            <Link to="/compte" data-testid="nav-account">
              <Button variant="ghost" className="rounded-full text-creme hover:bg-nuit-700 hover:text-or-300 px-2 sm:px-4">
                <User className="w-4 h-4" strokeWidth={2.5} />
              </Button>
            </Link>
            <Button data-testid="logout-button" onClick={handleLogout} variant="ghost"
              className="rounded-full text-brume hover:bg-rose-950 hover:text-rose-300 px-2 sm:px-4">
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
            </Button>
          </nav>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:grid lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-8">
        <SideColumn />
        <main className="mt-8 lg:mt-0 min-w-0">{children}</main>
      </div>
    </div>
  );
}
